import React from "react";
import { Flex, Text } from "styles/utility-components";

import Checkbox from "components/ui/checkbox/Checkbox";
import Divider from "components/ui/divider/Divider";
import NoticeStage from "lib/core_components/NoticeStage";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { tenderStages, TenderStageV2Groups, TenderStageV2GroupsKeys } from "lib/types/models";

type Props = {
  onChange: (values: NoticesStagesv2[]) => void;
  checkedStages: NoticesStagesv2[];
};

const grouped = Object.values(TenderStageV2Groups).map((group) => {
  const stages = Object.values(tenderStages).filter((stage) => stage.group === group);
  return {
    id: group,
    name: group.replace("_", "-").toLowerCase(),
    stages,
  };
});

const NoticeStageSelect = React.forwardRef<HTMLDivElement, Props>(
  ({ onChange, checkedStages }, ref) => {
    const handleSelect = (stageId: NoticesStagesv2) => {
      if (checkedStages.includes(stageId)) {
        onChange(checkedStages.filter((v) => v !== stageId));
      } else {
        onChange([...checkedStages, stageId]);
      }
    };

    const handleGroupSelectAll = (groupId: TenderStageV2GroupsKeys) => {
      const stageIdsInGroup = Object.values(tenderStages)
        .filter((stage) => stage.group === groupId)
        .map((stage) => stage.id);
      const stageIdsMissingInCurrentSelection = stageIdsInGroup.filter(
        (stageId) => !checkedStages.includes(stageId),
      );

      if (stageIdsMissingInCurrentSelection.length === 0) {
        onChange(checkedStages.filter((stage) => !stageIdsInGroup.includes(stage)));
      } else {
        onChange([...checkedStages, ...stageIdsMissingInCurrentSelection]);
      }
    };

    return (
      <Flex ref={ref} column gap="24px">
        {grouped.map((group, index) => (
          <Flex key={group.id} column gap="16px">
            {index !== 0 && <Divider margin="0" />}
            <Text color="sysTextBlack" textTransform="capitalize">
              {group.name}
            </Text>
            <Flex column gap="8px">
              <Flex alignItems="center" gap="8px">
                <Checkbox
                  checked={group.stages.every((stage) => checkedStages.includes(stage.id))}
                  onCheckedChange={() => handleGroupSelectAll(group.id)}
                  aria-labelledby={`stage-select-group-${group.id.toLowerCase()}`}
                />
                <Text
                  onClick={() => handleGroupSelectAll(group.id)}
                  id={`stage-select-group-${group.id.toLowerCase()}`}
                  aria-label={`Select All in Group ${group.name}`}
                >
                  Select All
                </Text>
              </Flex>
              {group.stages.map((stage) => (
                <Flex key={stage.id} alignItems="center" gap="8px">
                  <Checkbox
                    checked={checkedStages.includes(stage.id)}
                    onCheckedChange={() => handleSelect(stage.id)}
                    aria-labelledby={`stage-select-${stage.id.toLowerCase()}`}
                  />
                  <NoticeStage
                    stage={stage.id}
                    id={`stage-select-${stage.id.toLowerCase()}`}
                    onClick={() => handleSelect(stage.id)}
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>
        ))}
      </Flex>
    );
  },
);

NoticeStageSelect.displayName = "NoticeStageSelect";

export default NoticeStageSelect;
